import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, ArrowRight, Check, Mail, Users, Eye, Send,
  Search, CheckSquare, Square, AlertTriangle
} from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  fetchTemplateTypes, fetchTemplate, createCampaign, fetchCampaigns,
  addRecipients, fetchUsersForRecipients, fetchNotOnboardedUsers, sendTestEmail,
  confirmCampaign, startSending
} from '../../services/campaignService';

// Absolute URL for the brand logo used in the email header. Email clients
// cannot resolve relative paths, so this must be a full public URL. The logo
// (Original.png) lives in the frontend's public/ folder, served from this origin.
// Uses the origin the app is currently served from, which is correct in every
// environment (localhost in dev, dev./uat./prod hosts once deployed). An
// optional REACT_APP_PUBLIC_URL can override it if ever needed.
const PUBLIC_ORIGIN = (
  (typeof window !== 'undefined' && window.location ? window.location.origin : '') ||
  process.env.REACT_APP_PUBLIC_URL ||
  ''
).replace(/\/$/, '');
const EMAIL_LOGO_URL = `${PUBLIC_ORIGIN}/Original.png`;

/**
 * A campaign's natural audience.
 *
 * Every type except offboarding talks about a platform the reader is expected
 * to log in to, so a deactivated account is the wrong audience for it.
 *
 * Offboarding accepts anyone: sending it to an active user revokes their access
 * and then tells them, and sending it to an already-deactivated user is a
 * restatement of something that is already true. Either way the message is
 * accurate by the time it arrives, so there is nothing to filter out.
 *
 * Users loaded through the "Not Onboarded" filter come back without is_active
 * because that endpoint already restricts itself to active accounts — hence the
 * check against 0 rather than a truthiness test.
 */
function isEligibleRecipient(user, campaignType) {
  if (campaignType === 'offboarding') return true;
  return user.is_active !== 0;
}

const STEPS = [
  { id: 1, label: 'Type',       icon: Mail },
  { id: 2, label: 'Compose',    icon: Mail },
  { id: 3, label: 'Recipients', icon: Users },
  { id: 4, label: 'Review',     icon: Eye },
  { id: 5, label: 'Send',       icon: Send },
];

const CreateCampaign = ({ onBack, onCreated, preset = null }) => {
  const [step, setStep]               = useState(preset ? 2 : 1); // skip type selection if preset
  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  // Step 1: Type selection
  const [templateTypes, setTemplateTypes] = useState([]);
  const [selectedType, setSelectedType]   = useState(preset?.type || '');

  // Step 2: Compose
  const [subject, setSubject]       = useState('');
  const [bodyHtml, setBodyHtml]     = useState('');
  const [sendRate, setSendRate]     = useState(20);

  // Step 3: Recipients
  const [users, setUsers]                     = useState([]);
  const [selectedUsers, setSelectedUsers]     = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [roleFilter, setRoleFilter]           = useState(preset?.recipientFilter || '');
  const [usersLoading, setUsersLoading]       = useState(false);

  // Step 5: Send
  const [campaignId, setCampaignId]       = useState(null);
  const [testEmail, setTestEmail]         = useState(() => {
    const storedUser = JSON.parse(localStorage.getItem('act_user') || '{}');
    return storedUser?.email || '';
  });
  const [testResults, setTestResults]     = useState([]);
  const [isSending, setIsSending]         = useState(false);
  const [isConfirmed, setIsConfirmed]     = useState(false);
  const [campaignStarted, setCampaignStarted] = useState(false);

  // Load template types on mount
  useEffect(() => {
    loadTemplateTypes();
    // If preset type provided, load its template immediately
    if (preset?.type) {
      fetchTemplate(preset.type).then(template => {
        setSubject(template.subject || '');
        setBodyHtml(template.body_html || '');
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTemplateTypes() {
    try {
      const types = await fetchTemplateTypes();
      setTemplateTypes(types);

      // Check if Software Launch was already sent (to disable it on UI)
      try {
        const campaignData = await fetchCampaigns({ type: 'software_launch', status: 'completed', limit: 1 });
        const completedLaunch = campaignData.campaigns?.[0];
        if (completedLaunch) {
          setTemplateTypes(prev => prev.map(t =>
            t.type === 'software_launch'
              ? { ...t, disabled: true, disabledReason: `Already sent on ${new Date(completedLaunch.completed_at || completedLaunch.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` }
              : t
          ));
        }
      } catch {} // non-critical
    } catch (err) {
      setError('Failed to load template types');
    }
  }

  // Load template content when type is selected
  async function handleTypeSelect(type) {
    setSelectedType(type);
    setError('');
    try {
      const template = await fetchTemplate(type);
      setSubject(template.subject || '');
      setBodyHtml(template.body_html || '');
    } catch (err) {
      setError('Failed to load template');
    }
  }

  // Load users for recipient selection — auto-search on type with debounce
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      let allUsers = [];

      if (roleFilter === 'not_onboarded') {
        // Special filter: fetch users who never received onboarding email
        const data = await fetchNotOnboardedUsers();
        allUsers = data.users || [];
      } else {
        const data = await fetchUsersForRecipients({
          role: roleFilter || undefined,
          search: recipientSearch || undefined,
        });
        const userList = data.users || data;
        allUsers = Array.isArray(userList) ? userList : [];
      }

      // Exclude the currently logged-in user
      const storedUser = JSON.parse(localStorage.getItem('act_user') || '{}');
      const currentUserId = storedUser?.id;
      const filteredUsers = currentUserId
        ? allUsers.filter(u => u.id !== currentUserId)
        : allUsers;

      setUsers(filteredUsers);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [roleFilter, recipientSearch]);

  useEffect(() => {
    if (step === 3) {
      const timer = setTimeout(() => loadUsers(), 300); // debounce 300ms
      return () => clearTimeout(timer);
    }
  }, [step, loadUsers]);

  // Toggle user selection
  function toggleUser(user) {
    setSelectedUsers(prev => {
      const exists = prev.find(u => u.id === user.id);
      if (exists) return prev.filter(u => u.id !== user.id);
      return [...prev, user];
    });
  }

  // Everyone currently listed who suits this campaign type. Select All works on
  // this set only, so a stray deactivated account cannot be swept into a launch
  // or feature announcement by one click.
  const eligibleUsers = users.filter(u => isEligibleRecipient(u, selectedType));
  const allEligibleSelected = eligibleUsers.length > 0
    && eligibleUsers.every(e => selectedUsers.some(u => u.id === e.id));

  // Anything hand-picked that does not suit the campaign type — surfaced as a
  // warning rather than blocked, since there are legitimate one-off reasons.
  const mismatchedCount = selectedUsers.filter(u => !isEligibleRecipient(u, selectedType)).length;

  // Offboarding revokes access as it sends. This is how many of the chosen
  // recipients will actually lose their login — the rest are already inactive.
  const isOffboarding = selectedType === 'offboarding';
  const revokeCount   = isOffboarding
    ? selectedUsers.filter(u => u.is_active !== 0).length
    : 0;

  function toggleAll() {
    if (allEligibleSelected) {
      // Clear only what is on screen — selections made under another filter stay
      setSelectedUsers(prev => prev.filter(u => !eligibleUsers.some(e => e.id === u.id)));
      return;
    }
    // Add to the selection instead of replacing it, so a list can be built up
    // across several searches or role filters
    setSelectedUsers(prev => {
      const merged = [...prev];
      eligibleUsers.forEach(e => {
        if (!merged.some(u => u.id === e.id)) merged.push(e);
      });
      return merged;
    });
  }

  // Create campaign and add recipients (transition from step 4 → 5)
  async function handleCreateAndPrepare() {
    setIsLoading(true);
    setError('');
    try {
      // Auto-generate plain text from HTML
      const autoPlainText = htmlToPlainText(bodyHtml);

      // Create campaign
      const result = await createCampaign({
        type: selectedType,
        subject,
        body_html: bodyHtml,
        body_text: autoPlainText,
        send_rate: sendRate,
      });

      const newCampaignId = result.campaignId;
      setCampaignId(newCampaignId);

      // Add recipients
      const recipients = selectedUsers.map(u => ({
        user_id: u.id,
        email:   u.email,
        name:    u.name,
        role:    u.role,
      }));
      await addRecipients(newCampaignId, recipients);

      setStep(5);
    } catch (err) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setIsLoading(false);
    }
  }

  // Send test email
  async function handleTestSend() {
    if (!testEmail.trim()) {
      setError('Enter a test email address');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await sendTestEmail(campaignId, [testEmail.trim()]);
      setTestResults(result.results || []);
      setSuccess('Test email sent! Check your inbox.');
    } catch (err) {
      setError(err.message || 'Test send failed');
    } finally {
      setIsLoading(false);
    }
  }

  // Confirm campaign
  async function handleConfirm() {
    setIsLoading(true);
    setError('');
    try {
      await confirmCampaign(campaignId);
      setIsConfirmed(true);
      setSuccess('Campaign confirmed! Ready to send.');
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setIsLoading(false);
    }
  }

  // Start sending
  async function handleStartSending() {
    setIsSending(true);
    setError('');
    try {
      await startSending(campaignId);
      setCampaignStarted(true);
      setSuccess('Campaign sending started! Emails are being dispatched.');
    } catch (err) {
      setError(err.message || 'Failed to start sending');
    } finally {
      setIsSending(false);
    }
  }

  // Navigation
  function canProceed() {
    switch (step) {
      case 1: return !!selectedType;
      case 2: {
        // Quill empty state produces '<p><br></p>' — treat as empty
        const stripped = bodyHtml.replace(/<[^>]+>/g, '').replace(/\s/g, '');
        return !!subject.trim() && stripped.length > 0;
      }
      case 3: return selectedUsers.length > 0;
      case 4: return true;
      default: return false;
    }
  }

  function nextStep() {
    if (step === 4) {
      handleCreateAndPrepare();
      return;
    }
    setStep(s => Math.min(s + 1, 5));
    setError('');
    setSuccess('');
  }

  function prevStep() {
    setStep(s => Math.max(s - 1, 1));
    setError('');
    setSuccess('');
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Campaign</h1>
          <p className="text-sm text-gray-500">Step {step} of 5 — {STEPS[step - 1].label}{preset ? ' (Onboarding — pre-selected)' : ''}</p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center mb-8">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = step === s.id;
          const isComplete = step > s.id;
          return (
            <React.Fragment key={s.id}>
              <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                isActive ? 'bg-emerald-100 text-emerald-700' :
                isComplete ? 'bg-emerald-600 text-white' :
                'bg-gray-100 text-gray-500'
              }`}>
                {isComplete ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? 'bg-emerald-400' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Error / Success */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-emerald-700">{success}</p>
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">

        {/* ─── Step 1: Type Selection ─── */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose Campaign Type</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templateTypes.map(t => (
                <button
                  key={t.type}
                  onClick={() => !t.disabled && handleTypeSelect(t.type)}
                  disabled={t.disabled}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    t.disabled
                      ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                      : selectedType === t.type
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className={`font-semibold ${t.disabled ? 'text-gray-500' : 'text-gray-900'}`}>{t.label}</h3>
                    <div className="flex items-center space-x-2">
                      {t.oneTime && !t.disabled && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">One-time</span>
                      )}
                      {t.disabled && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Sent</span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{t.description}</p>
                  {t.disabledReason && (
                    <p className="text-xs text-emerald-600 mt-2 font-medium">{t.disabledReason}</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ─── Step 2: Compose ─── */}
        {step === 2 && (
          <ComposeStep
            subject={subject}
            setSubject={setSubject}
            bodyHtml={bodyHtml}
            setBodyHtml={setBodyHtml}
            sendRate={sendRate}
            setSendRate={setSendRate}
          />
        )}

        {/* ─── Step 3: Recipients ─── */}
        {step === 3 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Select Recipients
              <span className="ml-2 text-sm font-normal text-emerald-600">
                ({selectedUsers.length} selected)
              </span>
            </h2>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={recipientSearch}
                  onChange={e => setRecipientSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <select
                value={roleFilter}
                onChange={e => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Roles</option>
                <option value="not_onboarded">Not Onboarded</option>
                <option value="physician">Physician</option>
                <option value="sales">Sales</option>
                <option value="stakeholder">Stakeholder</option>
                <option value="superadmin">Superadmin</option>
              </select>
            </div>

            {/* Select All */}
            <div className="mb-3 pb-3 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <button onClick={toggleAll} className="flex items-center space-x-2 text-sm text-gray-600 hover:text-emerald-600">
                  {allEligibleSelected
                    ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                    : <Square className="w-4 h-4" />
                  }
                  <span>Select All ({eligibleUsers.length} users)</span>
                </button>
                {eligibleUsers.length !== users.length && (
                  <span className="text-xs text-gray-400">
                    — skips {users.length - eligibleUsers.length} deactivated user(s)
                  </span>
                )}
              </div>

              {mismatchedCount > 0 && (
                <div className="mt-2 flex items-start space-x-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    {mismatchedCount} selected recipient(s) are deactivated and cannot log in.
                    Remove them unless you meant to include them.
                  </p>
                </div>
              )}

              {isOffboarding && revokeCount > 0 && (
                <div className="mt-2 flex items-start space-x-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-800">
                    <strong>{revokeCount} of these users will lose access.</strong> Sending this
                    campaign deactivates each recipient before emailing them. You can restore
                    access afterwards from User Management.
                  </p>
                </div>
              )}
            </div>

            {/* User List */}
            {usersLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">No users found</p>
            ) : (
              <div className="max-h-[400px] overflow-y-auto space-y-1">
                {users.map(user => {
                  const isSelected = selectedUsers.some(u => u.id === user.id);
                  const isOnboarded = !!user.onboarded_at;
                  const isInactive  = user.is_active === 0;
                  return (
                    <button
                      key={user.id}
                      onClick={() => toggleUser(user)}
                      className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        isSelected ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      {isSelected
                        ? <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        : <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      }
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                          {isOnboarded && (
                            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                              Onboarded
                            </span>
                          )}
                          {isInactive && (
                            <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                              Inactive
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize flex-shrink-0">
                        {user.role}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── Step 4: Review ─── */}
        {step === 4 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Review Campaign</h2>

            {/* Repeated here because the campaign type can be changed after the
                recipients were picked — this is the last point before sending */}
            {isOffboarding && revokeCount > 0 && (
              <div className="mb-4 flex items-start space-x-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-800">
                  <strong>This campaign revokes access.</strong> {revokeCount} of these{' '}
                  {selectedUsers.length} recipients will be deactivated and will no longer be
                  able to log in. Superadmin accounts and your own account are never affected.
                </p>
              </div>
            )}

            {mismatchedCount > 0 && (
              <div className="mb-4 flex items-start space-x-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  {mismatchedCount} of these recipients are deactivated and cannot log in.
                  Go back and remove them unless you meant to include them.
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Type</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{selectedType.replace('_', ' ')}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Recipients</p>
                  <p className="text-sm font-medium text-gray-900">{selectedUsers.length} user(s)</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Subject</p>
                <p className="text-sm font-medium text-gray-900">{subject}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Sending To</p>
                <div className="mt-2 max-h-[150px] overflow-y-auto">
                  {selectedUsers.slice(0, 10).map(u => (
                    <p key={u.id} className="text-xs text-gray-600">
                      {u.name} ({u.email}) — <span className="capitalize">{u.role}</span>
                    </p>
                  ))}
                  {selectedUsers.length > 10 && (
                    <p className="text-xs text-gray-400 mt-1">... and {selectedUsers.length - 10} more</p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Email Preview</p>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <iframe
                    title="Email Preview"
                    srcDoc={bodyHtml}
                    className="w-full h-[400px] border-0"
                    sandbox=""
                  />
                </div>
              </div>
            </div>

            {/* Safety note */}
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-700">
                No emails will be sent yet. You'll be able to send a test email first and confirm before dispatching.
              </p>
            </div>
          </div>
        )}

        {/* ─── Step 5: Send ─── */}
        {step === 5 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Test &amp; Send</h2>

            {campaignStarted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Campaign is Sending!</h3>
                <p className="text-gray-600 mb-6">
                  Emails are being dispatched at {sendRate}/minute. You can monitor progress from the campaign detail page.
                </p>
                <button
                  onClick={onCreated}
                  className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                >
                  Back to Campaigns
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Test Send */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">1. Send Test Email</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Send a test to verify the email looks correct before sending to all recipients.
                  </p>
                  <div className="flex items-center space-x-2">
                    <input
                      type="email"
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <button
                      onClick={handleTestSend}
                      disabled={isLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {isLoading ? 'Sending...' : 'Send Test'}
                    </button>
                  </div>
                  {testResults.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {testResults.map((r, i) => (
                        <p key={i} className={`text-xs ${r.status === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {r.email}: {r.status} {r.error && `— ${r.error}`}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirm */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">2. Confirm Campaign</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Lock the campaign for sending. After confirming, the content cannot be edited.
                  </p>
                  <button
                    onClick={handleConfirm}
                    disabled={isLoading || isConfirmed}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      isConfirmed
                        ? 'bg-emerald-100 text-emerald-700 cursor-default'
                        : 'bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50'
                    }`}
                  >
                    {isConfirmed ? '✓ Confirmed' : isLoading ? 'Confirming...' : 'Confirm Campaign'}
                  </button>
                </div>

                {/* Start Sending */}
                <div className={`border rounded-lg p-4 ${isConfirmed ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 opacity-50'}`}>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">3. Start Sending</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    This will begin dispatching emails to all {selectedUsers.length} recipients at {sendRate} emails/minute.
                  </p>

                  {isOffboarding && revokeCount > 0 && (
                    <div className="mb-3 flex items-start space-x-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-800">
                        <strong>{revokeCount} user(s) will be deactivated as this sends.</strong>{' '}
                        Each one loses access first, then receives the notice. Access can be
                        restored from User Management.
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleStartSending}
                    disabled={!isConfirmed || isSending}
                    className={`px-6 py-2.5 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium ${
                      isOffboarding && revokeCount > 0
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                  >
                    {isSending
                      ? 'Starting...'
                      : isOffboarding && revokeCount > 0
                        ? `Revoke Access & Notify ${selectedUsers.length} Recipients`
                        : `Send to ${selectedUsers.length} Recipients`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      {step < 5 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={step === 1 ? onBack : prevStep}
            className="flex items-center space-x-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{step === 1 ? 'Cancel' : 'Back'}</span>
          </button>
          <button
            onClick={nextStep}
            disabled={!canProceed() || isLoading}
            className="flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            <span>{step === 4 ? (isLoading ? 'Creating...' : 'Create & Prepare') : 'Next'}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Compose Step Component (WYSIWYG Editor) ──────────────────────────────────

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'align', 'link',
];

/**
 * Extract editable text content from the branded HTML template.
 * Only extracts the paragraph/list content that the user should edit.
 * Strips CTA buttons, info boxes, and structural table HTML.
 */
function extractEditableContent(fullHtml) {
  // Find content between the padding:40px td and the footer td
  const match = fullHtml.match(/<td style="padding:40px;">([\s\S]*?)<\/td>\s*<\/tr>\s*<tr>\s*<td style="background-color:#f9fafb/);
  
  let innerContent = '';
  if (match && match[1]) {
    innerContent = match[1].trim();
  } else {
    // Fallback: try simpler match
    const simpleMatch = fullHtml.match(/<td style="padding:40px;">([\s\S]*?)<\/td>/);
    innerContent = simpleMatch ? simpleMatch[1].trim() : fullHtml;
  }

  // Remove ALL table-based elements (CTA buttons, info boxes, etc.)
  innerContent = innerContent.replace(/<table[\s\S]*?<\/table>/gi, '');

  // Clean up excessive whitespace
  innerContent = innerContent.replace(/\n{3,}/g, '\n\n').trim();

  return innerContent || '<p>Write your email content here...</p>';
}

/**
 * Rebuild the full template by replacing just the editable text content
 * while preserving the CTA button and info boxes from the original.
 */
function rebuildTemplate(originalHtml, newEditableContent) {
  // Find the content td in original
  const tdMatch = originalHtml.match(/(<td style="padding:40px;">)([\s\S]*?)(<\/td>\s*<\/tr>\s*(?:<tr>)?\s*<td style="background-color:#f9fafb)/);

  if (!tdMatch) {
    // Can't find structure — wrap in branded shell as fallback
    return wrapInBrandedShell(newEditableContent);
  }

  const originalInner = tdMatch[2].trim();

  // Extract preserved elements (tables = CTA buttons + info boxes) from original
  const preservedElements = [];
  const tableRegex = /<table[\s\S]*?<\/table>/gi;
  let tableMatch;
  while ((tableMatch = tableRegex.exec(originalInner)) !== null) {
    preservedElements.push(tableMatch[0]);
  }

  // Rebuild: new editable content + preserved elements
  const rebuiltInner = newEditableContent + '\n' + preservedElements.join('\n');

  return originalHtml.replace(
    /(<td style="padding:40px;">)([\s\S]*?)(<\/td>\s*<\/tr>\s*(?:<tr>)?\s*<td style="background-color:#f9fafb)/,
    `$1\n              ${rebuiltInner}\n            $3`
  );
}

/**
 * Re-wrap edited content back into the branded email shell (fallback).
 */
/**
 * Re-wrap edited content back into the branded email shell.
 */
function wrapInBrandedShell(innerContent) {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin:0;padding:0;background-color:#f4f7f6;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f7f6;padding:40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
          <tr>
            <td style="background:linear-gradient(135deg,#355699,#307a78);padding:28px 40px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto;">
                <tr>
                  <td style="vertical-align:middle;padding-right:8px;">
                    <img src="${EMAIL_LOGO_URL}" width="56" height="23" alt="ACTelligence" style="display:block;border:0;outline:none;text-decoration:none;height:auto;width:56px;" />
                  </td>
                  <td style="vertical-align:middle;text-align:left;">
                    <h1 style="margin:0;color:#ffffff;font-size:24px;font-weight:bold;letter-spacing:1px;line-height:1.1;">
                      ACT<em>elligence</em>
                    </h1>
                    <p style="margin:4px 0 0;color:rgba(255,255,255,0.8);font-size:13px;">
                      NexCAR19 Clinical Analytics Platform
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:40px;">
              ${innerContent}
            </td>
          </tr>
          <tr>
            <td style="background-color:#f9fafb;padding:20px 40px;border-top:1px solid #e5e7eb;text-align:center;">
              <p style="margin:0;color:#9ca3af;font-size:12px;">
                &copy; 2026 Immunoadoptive Cell Therapy Private Limited. All rights reserved.<br>
                This is an automated email, please do not reply.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * Auto-generate plain text from HTML (strip all tags)
 */
function htmlToPlainText(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&mdash;/g, '—')
    .replace(/&copy;/g, '©')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function ComposeStep({ subject, setSubject, bodyHtml, setBodyHtml, sendRate, setSendRate }) {
  // Extract editable content from the full branded template
  const [editorContent, setEditorContent] = useState(() => extractEditableContent(bodyHtml));
  const [showPreview, setShowPreview]     = useState(false);
  const quillRef = React.useRef(null);

  // Tracks the editable content we last synced into the editor.
  // Used to distinguish external template loads from our own rebuild echoes,
  // preventing an infinite render loop.
  const lastExtractedRef = React.useRef(extractEditableContent(bodyHtml));

  useEffect(() => {
    const extracted = extractEditableContent(bodyHtml);
    // Detect an external template load: the incoming bodyHtml's editable
    // content differs from what we last synced into the editor.
    if (extracted && extracted !== lastExtractedRef.current) {
      setEditorContent(extracted);
      lastExtractedRef.current = extracted;
      // Rebuild full HTML with the default CTA so the preview shows the button
      rebuildAndSet(extracted, true, 'Access ACTelligence', 'https://actelligence.act360.in/login');
    }
  }, [bodyHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // CTA Button controls
  const [showButton, setShowButton]     = useState(true);
  const [buttonText, setButtonText]     = useState('Access ACTelligence');
  const [buttonUrl, setButtonUrl]       = useState('https://actelligence.act360.in/login');

  // Rebuild full HTML from editor content + CTA button and update bodyHtml state
  function rebuildAndSet(content, btnShow, btnText, btnUrl) {
    const ctaHtml = btnShow
      ? `<table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:12px 0 28px;"><a href="${btnUrl}" style="display:inline-block;background:linear-gradient(135deg,#059669,#10b981);color:#ffffff;text-decoration:none;padding:16px 40px;border-radius:50px;font-size:15px;font-weight:600;letter-spacing:0.3px;box-shadow:0 4px 14px rgba(5,150,105,0.35);mso-padding-alt:0;">${btnText} &rarr;</a></td></tr></table>`
      : '';
    // content from Quill is just inner HTML (p tags, lists, etc.)
    // Combine content + CTA, then wrap in branded shell ONCE
    const innerBody = content + '\n' + ctaHtml;
    const fullHtml = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#f4f7f6;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f7f6;padding:40px 20px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background-color:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
        <tr><td style="background:linear-gradient(135deg,#355699,#307a78);padding:28px 40px;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto;">
            <tr>
              <td style="vertical-align:middle;padding-right:8px;"><img src="${EMAIL_LOGO_URL}" width="56" height="23" alt="ACTelligence" style="display:block;border:0;outline:none;text-decoration:none;height:auto;width:56px;" /></td>
              <td style="vertical-align:middle;text-align:left;">
                <h1 style="margin:0;color:#ffffff;font-size:24px;font-weight:bold;letter-spacing:1px;line-height:1.1;">ACT<em>elligence</em></h1>
                <p style="margin:4px 0 0;color:rgba(255,255,255,0.8);font-size:13px;">NexCAR19 Clinical Analytics Platform</p>
              </td>
            </tr>
          </table>
        </td></tr>
        <tr><td style="padding:40px;">
          ${innerBody}
        </td></tr>
        <tr><td style="background-color:#f9fafb;padding:20px 40px;border-top:1px solid #e5e7eb;text-align:center;">
          <p style="margin:0;color:#9ca3af;font-size:12px;">&copy; 2026 Immunoadoptive Cell Therapy Private Limited. All rights reserved.<br>This is an automated email, please do not reply.</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
    setBodyHtml(fullHtml);
    // Keep our "last extracted" ref in sync so the useEffect doesn't
    // treat this echo as an external template change
    lastExtractedRef.current = content;
  }

  function handleEditorChange(content) {
    setEditorContent(content);
    rebuildAndSet(content, showButton, buttonText, buttonUrl);
  }

  function handleButtonChange(newShowButton, newText, newUrl) {
    setShowButton(newShowButton);
    if (newText !== undefined) setButtonText(newText);
    if (newUrl !== undefined) setButtonUrl(newUrl);
    rebuildAndSet(
      editorContent,
      newShowButton,
      newText !== undefined ? newText : buttonText,
      newUrl !== undefined ? newUrl : buttonUrl
    );
  }

  function insertPlaceholder(placeholder) {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertText(range.index, `{{${placeholder}}}`);
      quill.setSelection(range.index + placeholder.length + 4);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Compose Email</h2>

      {/* Subject */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
        <input
          type="text"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          placeholder="Enter email subject..."
        />
      </div>

      {/* Send Rate */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Send Rate (emails/minute)
        </label>
        <input
          type="number"
          value={sendRate}
          onChange={e => setSendRate(Math.max(1, Math.min(30, parseInt(e.target.value) || 20)))}
          min="1"
          max="30"
          className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <p className="text-xs text-gray-400 mt-1">Recommended: 20. Max safe for M365: 30.</p>
      </div>

      {/* Visual Editor */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Email Content *</label>
        <p className="text-xs text-gray-400 mb-2">
          Edit the email content below. The ACTelligence branded header and footer are added automatically.
        </p>

        {/* Personalization Placeholder Buttons */}
        <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-xs font-medium text-blue-700">Insert personalization:</span>
          <button
            type="button"
            onClick={() => insertPlaceholder('name')}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors font-mono"
          >
            {'{{name}}'}
          </button>
          <button
            type="button"
            onClick={() => insertPlaceholder('full_name')}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors font-mono"
          >
            {'{{full_name}}'}
          </button>
          <button
            type="button"
            onClick={() => insertPlaceholder('role')}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors font-mono"
          >
            {'{{role}}'}
          </button>
          <button
            type="button"
            onClick={() => insertPlaceholder('set_password_link')}
            title="A one-time link, unique per recipient, letting them set their own password"
            className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors font-mono"
          >
            {'{{set_password_link}}'}
          </button>
          <span className="text-xs text-blue-500 ml-2">
            These get replaced with each recipient's actual data
          </span>
        </div>

        {/* The link placeholder is the one with a real effect, so it gets its own note */}
        {bodyHtml.includes('{{set_password_link}}') && (
          <div className="flex items-start space-x-2 mb-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
            <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-800">
              Each recipient gets their own single-use link, valid for 72 hours, letting them set a
              password and sign in. Test sends show a harmless example link instead of a working one.
            </p>
          </div>
        )}

        <div className="border border-gray-300 rounded-lg overflow-hidden">
          <ReactQuill
            ref={quillRef}
            value={editorContent}
            onChange={handleEditorChange}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            theme="snow"
            placeholder="Write your email content here..."
            style={{ minHeight: '300px' }}
          />
        </div>
      </div>

      {/* CTA Button Controls */}
      <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center space-x-3 mb-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showButton}
              onChange={e => handleButtonChange(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
          </label>
          <span className="text-sm font-medium text-gray-700">Include action button</span>
        </div>

        {showButton && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
              <input
                type="text"
                value={buttonText}
                onChange={e => handleButtonChange(showButton, e.target.value, undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g., Access ACTelligence"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Button URL</label>
              <input
                type="text"
                value={buttonUrl}
                onChange={e => handleButtonChange(showButton, undefined, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="https://actelligence.act360.in/login"
              />
            </div>
          </div>
        )}
      </div>

      {/* Preview Toggle */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
        >
          {showPreview ? 'Hide Preview' : 'Show Email Preview'}
        </button>

        {showPreview && (
          <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
            <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
              <p className="text-xs text-gray-500">This is how the email will look to recipients (placeholders like {'{{name}}'} will be replaced with actual names):</p>
            </div>
            <iframe
              title="Email Preview"
              srcDoc={bodyHtml}
              className="w-full h-[450px] border-0"
              sandbox=""
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateCampaign;
